// Ügyfél-detail „Szolgáltatások" szekció — az ügyfélhez rendelt szolgáltatás-csomagok (`?clientId={id}`).
//
// A LÉTREHOZÁS-oldalon ez a szekció ZÁROLTAN már látszik (`LockedSection`), a detail-oldalon viszont korábban
// EGYÁLTALÁN NEM volt ott. Ez félrevezető: a felhasználó megtanulja létrehozáskor, hogy „ez majd itt lesz",
// aztán a szerkesztő-oldalon hiába keresi. A négy kapcsolódó szekció ezért MINDKÉT oldalon, UGYANABBAN a
// sorrendben szerepel (Fiókok · Szolgáltatások · Számlázási profilok · Bankkártyák) — létrehozáskor zárolva,
// utána élesen. A szekció akkor is megjelenik, ha üres: a „nincs még" önmagában is információ.
//
// Három slot (`RelatedList`): **szolgáltatás (+ csomag alcím) · számlázási gyakoriság · státusz**.
import { type ServiceAssignmentRowSchema } from '@atlas/contracts';
import { PlusIcon, Square3Stack3DIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import {
  AssignmentInvoiceGroups,
  AssignmentViewToggle,
  type AssignmentSectionView,
} from '@/components/service-assignments/assignment-invoice-groups';
import { ServiceAssignmentFormSheet } from '@/components/service-assignments/service-assignment-form-sheet';
import { ServiceStatusBadge } from '@/components/service-assignments/service-status-badge';
import { FormSection } from '@/components/shared/form-section';
import { RelatedList, type RelatedListItem } from '@/components/shared/related-list';
import { Button } from '@/components/ui/button';
import { CLIENT_SECTION } from '@/features/clients/sections';
import { serviceAssignmentKeys } from '@/features/service-assignments/api';
import { api } from '@/lib/api';
import { assignmentSubLines } from '@/lib/service-plan-label';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

type ServiceAssignmentRow = z.infer<typeof ServiceAssignmentRowSchema>;

export function ClientServicesSection({
  clientId,
  clientName,
}: {
  clientId: string;
  clientName: string;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canManage = can('service_assignments.manage');

  // A sheet ITT nyílik (nem a lista-oldalra navigálunk): a kapcsolódó rekord felvétele ne szakítsa meg a
  // munkát a szülő ügyfélen. Az ügyfél a sheetben ZÁROLT — a szülő adott, nem választható át.
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<ServiceAssignmentRow | null>(null);
  // alapból a lapos lista; a „Számlánként" nézet a leendő számlák szerint csoportosít (user-döntés)
  const [view, setView] = useState<AssignmentSectionView>('list');

  /** A másodlagos sorok a KÖZÖS szabályból — a lista-oldal első oszlopával bitre azonos. */
  const subLines = (r: ServiceAssignmentRow): JSX.Element | undefined => {
    const lines = assignmentSubLines(r, t);
    if (lines.length === 0) return undefined;
    return (
      <>
        {lines.map((line) => (
          <div key={line} className="truncate" title={line}>
            {line}
          </div>
        ))}
      </>
    );
  };

  const listKey = [...serviceAssignmentKeys.lists(), 'by-client', clientId];
  const query = api.serviceAssignments.useQuery(listKey, {
    query: { clientId, limit: 200 },
  });
  const rows: ServiceAssignmentRow[] =
    query.data?.status === 200 ? query.data.body.assignments : [];
  const errStatus = query.error?.status;

  const items: RelatedListItem[] = rows.map((r) => ({
    id: r.id,
    title: r.serviceName ?? r.planName ?? '—',
    // AZONOS NEVEK a hozzárendelés-lista első oszlopával (user-döntés): a csomag CSAK ha eltér, plusz a
    // komplexitás és a HASZNÁLAT FORRÁSA. Korábban itt a csomag-név akkor is kiült, ha azonos volt a
    // szolgáltatáséval (önismétlés), a forrás pedig egyáltalán nem látszott — ugyanaz a jutalékos sor
    // jelenthet direkt foglalást vagy ajánlatot.
    subtitle: subLines(r),
    // Fiók-szintű hozzárendelésnél a fiók a legfontosabb kiegészítő infó, egyébként a számlázási gyakoriság.
    meta:
      r.accountName ??
      (r.billingFrequency
        ? t(`serviceAssignments.frequencyLabel.${r.billingFrequency}`, {
            defaultValue: r.billingFrequency,
          })
        : undefined),
    status: (
      <ServiceStatusBadge
        status={r.status}
        label={t(`serviceAssignments.statusLabel.${r.status}`, { defaultValue: r.status })}
      />
    ),
  }));

  return (
    <FormSection
      id={CLIENT_SECTION.services}
      title={t('clients.servicesTitle')}
      action={<AssignmentViewToggle value={view} onChange={setView} t={t} />}
    >
      {view === 'invoices' ? (
        <AssignmentInvoiceGroups target={{ clientId }} t={t} />
      ) : (
        <RelatedList
          items={items}
          isLoading={query.isLoading}
          isError={query.isError}
          forbidden={isForbiddenStatus(errStatus)}
          onRetry={() => void query.refetch()}
          // Jog nélkül a sor NEM kattintható: a sheet a szerkesztőt nyitná meg, amit a BFF úgyis 403-mal zárna.
          onItemClick={
            canManage ? (item) => setEditRow(rows.find((r) => r.id === item.id) ?? null) : undefined
          }
          // MIND A HOZZÁRENDELÉS LÁTSZIK (user-döntés): eddig 8 után „Mind a 61 mutatása" állt. Élesben a
          // legtöbb egy ügyfélen 61 sor, az átlag 7,4 — kirenderelve is kezelhető szekció, és a kérdés
          // („mit kap ez az ügyfél?") pont a TELJES listát kívánja.
          maxVisible={Infinity}
          showAllLabel={(total) => t('clients.relatedShowAll', { count: total })}
          // A LÁB-LINK a SZŰRT hozzárendelés-listára visz — ott vannak a szűrők, az oszlopok és a sor-akciók.
          footerAction={{
            label: t('common.openFilteredList'),
            onClick: () =>
              void navigate({
                to: '/services',
                search: { tab: 'assignments', asg_client: clientId },
              }),
          }}
          emptyLabel={t('clients.servicesEmpty')}
          emptyIcon={Square3Stack3DIcon}
          errorLabel={t('clients.relatedError')}
          forbiddenLabel={t('clients.relatedForbidden')}
          retryLabel={t('clients.relatedRetry')}
        />
      )}

      {/* A „hozzáadás" belépő a szekció TARTALMÁNAK VÉGÉN, BALRA (user-döntés, 2026-08-18) — ugyanaz a
          gomb-alak, mint a profil „Jelszó módosítása" / „2FA beállítása" gombja. Korábban a fejléc jobb
          szélén ült: ott vizuálisan nehezebb volt a halk `overline` címnél, és négy szekciónál egy egész
          gomb-oszlop uralta a lapot. A tartalom végén viszont ott van, ahol a felhasználó a lista átnézése
          UTÁN jár. Nincs `mt-*` — a térközt a `FormSection` `space-y-4`-e adja. */}
      {canManage && (
        <div className="flex">
          <Button type="button" variant="outline" onClick={() => setCreateOpen(true)}>
            <PlusIcon />
            {t('clients.addService')}
          </Button>
        </div>
      )}

      <ServiceAssignmentFormSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        assignment={null}
        lockedClient={{ id: clientId, name: clientName }}
      />
      <ServiceAssignmentFormSheet
        open={editRow !== null}
        onOpenChange={(o) => !o && setEditRow(null)}
        assignment={editRow}
      />
    </FormSection>
  );
}
